import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { cloneBlendIf, cloneBlendIfRange } from '../../core/blendIf';
import type { BlendIf, BlendIfChannel, BlendIfRange } from '../../core/types';
import { Select } from '../components/controls';

const CHANNEL_OPTIONS: { value: BlendIfChannel; label: string }[] = [
  { value: 'gray', label: 'Gray' },
  { value: 'red', label: 'Red' },
  { value: 'green', label: 'Green' },
  { value: 'blue', label: 'Blue' },
];

type HandleId = 'black0' | 'black1' | 'white0' | 'white1';

function clampByte(value: number): number {
  return Math.min(255, Math.max(0, Math.round(value)));
}

function applyHandle(
  range: BlendIfRange,
  handle: HandleId,
  value: number,
  split: boolean,
): BlendIfRange {
  const next = { ...range };
  const v = clampByte(value);

  if (handle === 'black0' || handle === 'black1') {
    const unsplit = next.black0 === next.black1;
    if (split && unsplit) {
      next.black0 = next.black1;
      next.black1 = v;
    } else if (!split && unsplit) {
      next.black0 = v;
      next.black1 = v;
    } else if (handle === 'black0') {
      next.black0 = Math.min(v, next.black1);
    } else {
      next.black1 = Math.max(v, next.black0);
    }
    if (next.black1 > next.white0) {
      next.white0 = next.black1;
      if (next.white1 < next.white0) next.white1 = next.white0;
    }
  } else {
    const unsplit = next.white0 === next.white1;
    if (split && unsplit) {
      next.white1 = next.white0;
      next.white0 = v;
    } else if (!split && unsplit) {
      next.white0 = v;
      next.white1 = v;
    } else if (handle === 'white0') {
      next.white0 = Math.min(v, next.white1);
    } else {
      next.white1 = Math.max(v, next.white0);
    }
    if (next.white0 < next.black1) {
      next.black1 = next.white0;
      if (next.black0 > next.black1) next.black0 = next.black1;
    }
  }

  return cloneBlendIfRange(next);
}

function nearestHandle(range: BlendIfRange, value: number): HandleId {
  const candidates: { id: HandleId; at: number }[] = [
    { id: 'black0', at: range.black0 },
    { id: 'black1', at: range.black1 },
    { id: 'white0', at: range.white0 },
    { id: 'white1', at: range.white1 },
  ];
  let best = candidates[0];
  let bestDist = Math.abs(value - best.at);
  for (const entry of candidates.slice(1)) {
    const dist = Math.abs(value - entry.at);
    if (dist < bestDist) {
      best = entry;
      bestDist = dist;
    }
  }
  return best.id;
}

function formatEnd(a: number, b: number): string {
  return a === b ? String(a) : `${a}/${b}`;
}

function BlendIfSlider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: BlendIfRange;
  onChange: (next: BlendIfRange) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ handle: HandleId; split: boolean } | null>(null);

  const valueFromClientX = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const t = rect.width <= 0 ? 0 : (clientX - rect.left) / rect.width;
    return clampByte(t * 255);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const at = valueFromClientX(event.clientX);
    const handle = nearestHandle(value, at);
    const split = event.altKey;
    dragRef.current = { handle, split };
    onChange(applyHandle(value, handle, at, split));
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const { handle, split } = dragRef.current;
    onChange(applyHandle(value, handle, valueFromClientX(event.clientX), split));
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const blackSplit = value.black0 !== value.black1;
  const whiteSplit = value.white0 !== value.white1;

  return (
    <div className="blend-if-slider">
      <div className="blend-if-slider__label">{label}</div>
      <div className="blend-if-slider__body">
        <div className="blend-if-slider__scale">
          <span>0</span>
          <span>255</span>
        </div>
        <div
          ref={trackRef}
          className="blend-if-slider__track"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          title="Drag handles; Alt-drag to split for a soft transition"
        >
          <span
            className={`blend-if-slider__handle blend-if-slider__handle--black${blackSplit ? ' blend-if-slider__handle--split' : ''}`}
            style={{ left: `${(value.black0 / 255) * 100}%` }}
          />
          {blackSplit && (
            <span
              className="blend-if-slider__handle blend-if-slider__handle--black blend-if-slider__handle--split"
              style={{ left: `${(value.black1 / 255) * 100}%` }}
            />
          )}
          <span
            className={`blend-if-slider__handle blend-if-slider__handle--white${whiteSplit ? ' blend-if-slider__handle--split' : ''}`}
            style={{ left: `${(value.white0 / 255) * 100}%` }}
          />
          {whiteSplit && (
            <span
              className="blend-if-slider__handle blend-if-slider__handle--white blend-if-slider__handle--split"
              style={{ left: `${(value.white1 / 255) * 100}%` }}
            />
          )}
        </div>
        <div className="blend-if-slider__values">
          <span>{formatEnd(value.black0, value.black1)}</span>
          <span>{formatEnd(value.white0, value.white1)}</span>
        </div>
      </div>
    </div>
  );
}

export function BlendIfControls({
  value,
  onChange,
}: {
  value: BlendIf;
  onChange: (next: BlendIf) => void;
}) {
  const blendIf = cloneBlendIf(value);
  return (
    <div className="blend-if">
      <div className="field">
        <span className="field__label">Blend If</span>
        <Select
          value={blendIf.channel}
          options={CHANNEL_OPTIONS}
          onChange={(channel) => onChange({ ...blendIf, channel })}
        />
      </div>
      <BlendIfSlider
        label="This Layer"
        value={blendIf.thisLayer}
        onChange={(thisLayer) => onChange({ ...blendIf, thisLayer })}
      />
      <BlendIfSlider
        label="Underlying Layer"
        value={blendIf.underlying}
        onChange={(underlying) => onChange({ ...blendIf, underlying })}
      />
    </div>
  );
}
